import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BatchJobProgress } from '../../components/BatchJobProgress';
import { ensureCsrf, getCachedCsrf } from '../../utils/csrf';

interface PreviewStepProps {
  sessionId: string;
  onDone?: () => void;
  onBack?: () => void;
  containsDates?: boolean;
  monthStart?: string;
}

interface SummaryItem {
  customer_lookup_key: string;
  count: number;
  invalid: number;
}

interface GeneratedInvoice {
  id: string;
  customer_name: string;
  total: number;
}

export default function PreviewStep({
  sessionId,
  onDone,
  onBack,
  containsDates = true,
  monthStart,
}: PreviewStepProps) {
  const [summary, setSummary] = useState<SummaryItem[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [generatedInvoices, setGeneratedInvoices] = useState<GeneratedInvoice[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [overrideDescriptions, setOverrideDescriptions] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [showProgress, setShowProgress] = useState(false);
  const [exportBatchId, setExportBatchId] = useState<string | null>(null);
  const [sessionActive, setSessionActive] = useState(false);
  const isProgressOpen = showProgress && !!exportBatchId;

  useEffect(() => {
    if (isProgressOpen) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/bulk-invoices/status/${sessionId}`, {
          credentials: 'include',
          headers: {
            ...(isCookieMode || !token ? {} : { Authorization: `Bearer ${token}` }),
          },
        });

        if (res.ok) {
          const payload = await res.json();
          const active = Boolean(payload?.active);
          setSessionActive(active);
          if (active && payload?.exportJobId) {
            setExportBatchId((prev) => prev || payload.exportJobId);
            setShowProgress(true);
          }
        }
      } catch (_) {}

      timer = setTimeout(poll, 1000);
    };

    void poll();

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [API_BASE_URL, currentBusiness?.id, isCookieMode, isProgressOpen, sessionId, token]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/service-drafts/summary?sessionId=${sessionId}`, {
      credentials: 'include',
      headers: {
        ...(isCookieMode || !token ? {} : { Authorization: `Bearer ${token}` }),
      },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setSummary(Array.isArray(data) ? data : []))
      .catch(() => setSummary([]));

    fetch(`${API_BASE_URL}/service-drafts/groups?sessionId=${sessionId}`, {
      credentials: 'include',
      headers: {
        ...(isCookieMode || !token ? {} : { Authorization: `Bearer ${token}` }),
      },
    })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .catch(() => setGroups([]));
  }, [API_BASE_URL, isCookieMode, sessionId, token]);

  const handleGenerate = async () => {
    if (isGenerating || sessionActive) {
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const csrfToken = getCachedCsrf() ?? (await ensureCsrf(API_BASE_URL));
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/bulk-invoices/generate`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          sessionId,
          invoiceDate: new Date().toISOString().slice(0, 10),
          overrideDescriptions,
          options: { containsDates, monthStart },
        }),
      });

      const responseText = await response.text();
      let payload: any = null;
      try {
        payload = responseText ? JSON.parse(responseText) : null;
      } catch (_) {
        payload = null;
      }

      if (!response.ok) {
        if (response.status === 409 && payload?.code === 'BULK_INVOICE_ACTIVE' && payload?.exportBatchId) {
          setExportBatchId(payload.exportBatchId);
          setShowProgress(true);
          return;
        }

        if (response.status === 409 && payload?.code === 'DRAFT_SESSION_LOCKED') {
          setError(payload?.error || 'This draft session already generated invoices. Start a new draft session to generate again.');
          return;
        }

        throw new Error(payload?.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (payload?.exportBatchId) {
        setExportBatchId(payload.exportBatchId);
        setShowProgress(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate invoices');
    } finally {
      setIsGenerating(false);
    }
  };

  const groupsByCustomer = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const group of groups) {
      const key = group.customer_lookup_key || group.customer_id || 'Unknown';
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(group);
    }
    return map;
  }, [groups]);

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  void isLoadingInvoices;
  void generatedInvoices;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold mb-4">Preview &amp; Generate</h1>

      {error && <div className="mb-4 text-red-600">{error}</div>}

      <div className="mb-6 rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
        <div className="p-5">
          <h2 className="text-lg font-semibold mb-2">Descriptions {containsDates ? '' : '(No-dates mode)'}</h2>
        </div>
        <div className="divide-y">
          {Object.entries(groupsByCustomer).length === 0 ? (
            <div className="p-5 text-sm text-gray-500">No grouped lines found.</div>
          ) : (
            Object.entries(groupsByCustomer).map(([custKey, list]) => {
              const isOpen = !!expanded[custKey];
              const summaryMatch = summary.find((item) => item.customer_lookup_key === custKey);
              const headerText = summaryMatch
                ? `${summaryMatch.customer_lookup_key} - ${summaryMatch.count} draft(s)`
                : String(custKey);

              return (
                <div key={custKey}>
                  <button
                    className="w-full text-left px-5 py-3 bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
                    onClick={() => toggle(custKey)}
                  >
                    <span className="font-medium text-sm">{headerText}</span>
                    <span className="text-xs text-gray-600">{isOpen ? 'Hide' : 'Show'}</span>
                  </button>
                  {isOpen && (
                    <div className="p-5 space-y-3">
                      {list.map((group) => {
                        const monthLabel = (() => {
                          try {
                            const [year, month] = String(group.month_date)
                              .slice(0, 7)
                              .split('-')
                              .map((value: string) => Number(value));
                            return new Date(year, (month || 1) - 1, 1).toLocaleDateString(undefined, {
                              month: 'long',
                              year: 'numeric',
                            });
                          } catch {
                            return String(group.month_date);
                          }
                        })();
                        const qty = containsDates
                          ? (Array.isArray(group.dates) ? group.dates.length : 0)
                          : (Number(group.qty_sum) || (Array.isArray(group.dates) ? group.dates.length : 0));
                        const computedDefault = (() => {
                          if (containsDates) {
                            const base = String(group.defaultDescription || '');
                            const extraText = group.top_raw_text && String(group.top_raw_text).trim();
                            if (extraText && !base.includes(extraText)) {
                              return `${base} -- ${extraText}`;
                            }
                            return base;
                          }

                          const label = group.item_name || group.service_type_id;
                          const base = `${monthLabel} -- ${label}${qty ? ` (${qty})` : ''}`;
                          const extra = group.top_raw_text && String(group.top_raw_text).trim()
                            ? ` -- ${String(group.top_raw_text).trim()}`
                            : '';
                          return `${base}${extra}`;
                        })();
                        const value = overrideDescriptions[group.key] ?? computedDefault;

                        return (
                          <div key={group.key} className="p-4 rounded-2xl border border-gray-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="text-sm font-medium">{group.customer_label || group.customer_lookup_key}</div>
                              <div className="text-xs text-gray-500">
                                {containsDates ? (Array.isArray(group.dates) ? group.dates.length : 0) : (Number(group.qty_sum) || 0)} item(s)
                              </div>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs text-gray-500 mb-1">Description</label>
                              <input
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sedra"
                                value={value}
                                onChange={(e) => setOverrideDescriptions((prev) => ({ ...prev, [group.key]: e.target.value }))}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex justify-between">
        <button className="rounded-xl border border-gray-300 px-4 py-2 text-sm" onClick={onBack}>Back</button>
        <button
          className="rounded-xl bg-sedra text-white px-4 py-2 text-sm font-semibold hover:bg-sedra-700 disabled:opacity-50"
          onClick={handleGenerate}
          disabled={isGenerating || sessionActive}
        >
          {isGenerating ? 'Generating...' : (sessionActive ? 'Generating...' : 'Generate Invoices')}
        </button>
      </div>

      {showProgress && (
        <BatchJobProgress
          batchId={exportBatchId}
          onClose={() => {
            setShowProgress(false);
            setExportBatchId(null);
            onDone?.();
          }}
          onComplete={() => {}}
        />
      )}
    </div>
  );
}
